import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import PageTitle2 from "../../components/pagetitle/PageTitle2";
import Header from "../../components/header/Header";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import RetailBusiness from "../../components/WorkWithTCUK/RetailBusiness";
import RetailAgent from "../../components/WorkWithTCUK/RetailAgent";
import { FaLink, FaCopy, FaSearch, FaTimes } from "react-icons/fa";
import { IoArrowForward, IoArrowBack } from "react-icons/io5";
import { API_URL } from "../../constant";
import { useLocation } from "react-router-dom";

function WorkWithTCUKPage(props) {
  // Senior Agents state
  const [seniorAgents, setSeniorAgents] = useState([]);
  const [displayAgents, setDisplayAgents] = useState([]); // Agents to display
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [agentsError, setAgentsError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // Search input value
  const location = useLocation();
  const [isPageReady, setIsPageReady] = useState(false);

  // New state for hover preview - Updated for static positioning
  const [hoveredAgent, setHoveredAgent] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false); // Control visibility separately

  // State for marquee animation
  const [marqueeAnimation, setMarqueeAnimation] = useState("none");
  const [isMarqueePaused, setIsMarqueePaused] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const [lastInteractionTime, setLastInteractionTime] = useState(Date.now());

  //search error
  const [searchError, setSearchError] = useState("");

  const [displayBusinessAgents, setDisplayBusinessAgents] = useState([]);
  const [isLoadingBusinessAgents, setIsLoadingBusinessAgents] = useState(true);
  const [businessAgentsError, setBusinessAgentsError] = useState(null);

  const [filters, setFilters] = useState({
    per_page: 20,
    page: 1,
    country: "",
    region: "",
    search: "",
    sort_by: "created_at",
    sort_order: "asc",
  });

  // State for selected user type (retail-business or retail-agent)
  const [selectedUserType, setSelectedUserType] = useState("retail-business");

  const slideIntervalRef = useRef(null);
  const searchInputRef = useRef(null); // Ref for search input
  const marqueeRef = useRef(null); // Ref for marquee container
  const agentNameRefs = useRef([]); // Refs for agent name elements
  const marqueeContentRef = useRef(null); // Ref for marquee content
  const marqueeResizeObserver = useRef(null); // For observing size changes
  const hoverTimeoutRef = useRef(null); // Ref for hover timeout - Added

  // Combined scroll handler for all hash anchors
  const scrollToHashElement = useCallback(() => {
    const hash = window.location.hash;
    if (!hash) return;

    // Remove the # from the hash
    const elementId = hash.replace("#", "");

    // Wait for next tick to ensure DOM is updated
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        const yOffset = -80; // Adjust for header
        const y =
          element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
        return true;
      }

      // If element not found immediately, try after a short delay
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const yOffset = -80;
          const y =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    }, 100);
  }, []);

  // Focus search input on page load/refresh
  useEffect(() => {
    // Focus the search input after components are fully loaded
    const focusTimer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 300); // 300ms delay to ensure everything is loaded

    return () => clearTimeout(focusTimer);
  }, []);

  // Handle initial page load with hash
  useEffect(() => {
    // Set a flag when component is mounted and data is loaded
    const timer = setTimeout(() => {
      setIsPageReady(true);
    }, 800); // Adjust delay based on content load time

    return () => clearTimeout(timer);
  }, []);

  // Handle hash changes and initial load
  useEffect(() => {
    if (!isPageReady) return;

    // Check if there's a hash in the URL
    if (location.hash) {
      // Scroll after a brief delay to ensure all content is rendered
      const scrollTimer = setTimeout(() => {
        scrollToHashElement();
      }, 300);

      return () => clearTimeout(scrollTimer);
    }
  }, [location.hash, isPageReady, scrollToHashElement]);

  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach((key) => {
    if (
      filters[key] !== undefined &&
      filters[key] !== "" &&
      filters[key] !== "all"
    ) {
      queryParams.append(key, filters[key]);
    }
  });

  // Function to fetch senior agents from API
  const fetchSeniorAgents = useCallback(async (userType = selectedUserType) => {
    try {
      setIsLoadingAgents(true);
      setAgentsError(null);

      const url = `${API_URL}/api/senior-retail-business-agents/public${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Format agents for UI
      const colors = [
        "#2D4CA8",
        "#9F4AFF",
        "#FF6B6B",
        "#4CAF50",
        "#FF9800",
        "#9C27B0",
        "#2196F3",
        "#FF5722",
      ];
      const formattedAgents = data.agents.map((agent, index) => {
        // Generate different referral links based on user type
        const retailBusinessLink = `${window.location.origin}/retail-business-signup?ref=${agent.referral_code}`;
        const retailAgentLink = `${window.location.origin}/retail-agent-signup?ref=${agent.referral_code}`;

        // Generate QR code URL for the selected referral link
        const selectedLink =
          userType === "retail-business" ? retailBusinessLink : retailAgentLink;
        const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
          selectedLink,
        )}`;

        // Generate a color based on agent ID for UI consistency
        const colorIndex = agent.agent_id
          ? agent.agent_id % colors.length
          : index % colors.length;

        return {
          id: agent.agent_id || agent.customer_id,
          customer_id: agent.customer_id,
          agent_id: agent.agent_id,
          fullName: agent.full_name || `${agent.first_name} ${agent.last_name}`,
          firstName: agent.first_name,
          lastName: agent.last_name,
          location: agent.country || "Unknown",
          region: agent.region,
          position: agent.position || "Senior Retail Business Agent",
          retailBusinessLink: retailBusinessLink,
          retailAgentLink: retailAgentLink,
          qrCode: qrCodeUrl,
          profilePic:
            agent.profile_image ||
            `https://randomuser.me/api/portraits/${
              index % 2 === 0 ? "men" : "women"
            }/${(index % 50) + 1}.jpg`,
          color: colors[colorIndex],
          email: agent.email,
          phoneNumber: agent.phone_number,
          memberSince: agent.member_since,
          agentSince: agent.agent_since,
          isActive: agent.is_active,
          agentCode: agent.referral_code,
          shortName:
            agent.first_name + " " + agent.last_name ||
            agent.fullName?.split(" ")[0]?.charAt(0) +
              ". " +
              agent.fullName?.split(" ")[1] ||
            agent.fullName,
        };
      });

      setSeniorAgents(formattedAgents);
      setDisplayAgents(formattedAgents); // Set display agents to all agents initially

      // Reset slide to 0 when new data loads
      setCurrentSlide(0);
    } catch (error) {
      console.error("Failed to fetch senior agents:", error);
      setAgentsError(
        "Failed to load senior agents. Showing sample data instead.",
      );

      // Fallback to dummy data if API fails
      const fallbackData = [
        {
          id: 1,
          fullName: "Era Morgan",
          shortName: "E. Morgan",
          location: "UK",
          position: "Senior Retail Business Agent",
          retailBusinessLink: `${window.location.origin}/retail-business-signup?ref=6321`,
          retailAgentLink: `${window.location.origin}/retail-agent-signup?ref=6321`,
          qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
            `${window.location.origin}/retail-business-signup?ref=6321`,
          )}`,
          profilePic: "https://randomuser.me/api/portraits/women/65.jpg",
          color: "#2D4CA8",
        },
        {
          id: 2,
          fullName: "Malcolm Summers",
          shortName: "M. Summers",
          location: "Sri Lanka",
          position: "Senior Retail Business Agent",
          retailBusinessLink: `${window.location.origin}/retail-business-signup?ref=2116`,
          retailAgentLink: `${window.location.origin}/retail-agent-signup?ref=2116`,
          qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
            `${window.location.origin}/retail-business-signup?ref=2116`,
          )}`,
          profilePic: "https://randomuser.me/api/portraits/men/32.jpg",
          color: "#9F4AFF",
        },
        {
          id: 3,
          fullName: "Kathy Godridge",
          shortName: "K. Godridge",
          location: "Switzerland",
          position: "Senior Retail Business Agent",
          retailBusinessLink: `${window.location.origin}/retail-business-signup?ref=1234`,
          retailAgentLink: `${window.location.origin}/retail-agent-signup?ref=1234`,
          qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
            `${window.location.origin}/retail-business-signup?ref=1234`,
          )}`,
          profilePic: "https://randomuser.me/api/portraits/women/44.jpg",
          color: "#FF6B6B",
        },
        {
          id: 4,
          fullName: "Alex Johnson",
          shortName: "A. Johnson",
          location: "Australia",
          position: "Senior Retail Business Agent",
          retailBusinessLink: `${window.location.origin}/retail-business-signup?ref=3215`,
          retailAgentLink: `${window.location.origin}/retail-agent-signup?ref=3215`,
          qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
            `${window.location.origin}/retail-business-signup?ref=3215`,
          )}`,
          profilePic: "https://randomuser.me/api/portraits/men/67.jpg",
          color: "#4CAF50",
        },
        {
          id: 5,
          fullName: "Sarah Williams",
          shortName: "S. Williams",
          location: "Germany",
          position: "Senior Retail Business Agent",
          retailBusinessLink: `${window.location.origin}/retail-business-signup?ref=4521`,
          retailAgentLink: `${window.location.origin}/retail-agent-signup?ref=4521`,
          qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
            `${window.location.origin}/retail-business-signup?ref=4521`,
          )}`,
          profilePic: "https://randomuser.me/api/portraits/women/33.jpg",
          color: "#FF9800",
        },
      ];
      setSeniorAgents(fallbackData);
      setDisplayAgents(fallbackData);
    } finally {
      setIsLoadingAgents(false);
    }
  }, []); // Empty dependency array - function is stable

  const fetchBusinessAgents = async () => {
  try {
    setIsLoadingBusinessAgents(true);
    setBusinessAgentsError(null);

    const response = await fetch(
      `${API_URL}/api/public/retail-businesses/agents`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("Business Agents API Response:", data); // Add this line to debug

    const formattedBusinessAgents = (data.agents || []).map((agent) => ({
      id: agent.agent_id,
      fullName: agent.full_name || `${agent.first_name || ""} ${agent.last_name || ""}`.trim(),
      location: agent.country || "Unknown",
    }));

    console.log("Formatted Business Agents:", formattedBusinessAgents); // Add this line to debug

    setDisplayBusinessAgents(formattedBusinessAgents);
  } catch (error) {
    console.error("Error fetching business agents:", error);
    setBusinessAgentsError("Failed to load business agents.");
  } finally {
    setIsLoadingBusinessAgents(false);
  }
};
  useEffect(() => {
    fetchBusinessAgents();
  }, []);

  // Function to start marquee animation
  const startMarqueeAnimation = useCallback(() => {
    if (!marqueeContentRef.current || !marqueeRef.current) return;

    const containerWidth = marqueeRef.current.offsetWidth;
    const contentWidth = marqueeContentRef.current.scrollWidth;

    // Only animate if content is wider than container
    if (contentWidth > containerWidth && !isMarqueePaused) {
      const duration = Math.max(20, (contentWidth / 100) * 1.5); // Calculate duration based on width
      const animation = `marquee ${duration}s linear infinite`;
      setMarqueeAnimation(animation);

      if (!isMarqueePaused) {
        marqueeContentRef.current.style.transform = "translateX(0)";
        marqueeContentRef.current.style.transition = "none";
        marqueeContentRef.current.style.animation = animation;
        setScrollPosition(0);
      }
    } else {
      setMarqueeAnimation("none");
    }
  }, [isMarqueePaused]);

  // Clean up hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Initialize sliding interval
  useEffect(() => {
    // Check if we should auto-slide
    const shouldAutoSlide =
      isAutoSliding &&
      displayAgents.length > 0 &&
      (!window.matchMedia("(min-width: 992px)").matches ||
        displayAgents.length >= 3);

    if (shouldAutoSlide) {
      slideIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % displayAgents.length);
      }, 4000);
    } else if (slideIntervalRef.current) {
      // Clear interval if conditions not met
      clearInterval(slideIntervalRef.current);
      slideIntervalRef.current = null;
    }

    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
    };
  }, [isAutoSliding, displayAgents.length]);

  // Fetch agents on component mount
  useEffect(() => {
    fetchSeniorAgents();
  }, []); // Empty dependency array - only run once on mount

  // Start marquee animation when displayAgents changes
  useEffect(() => {
    // Use setTimeout to ensure DOM is updated
    const timer = setTimeout(() => {
      startMarqueeAnimation();
    }, 100);

    return () => clearTimeout(timer);
  }, [displayAgents, startMarqueeAnimation]);

  // Handle resize and start marquee
  useEffect(() => {
    const handleResize = () => {
      startMarqueeAnimation();
      // Recalculate max scroll
      if (marqueeContentRef.current && marqueeRef.current) {
        const containerWidth = marqueeRef.current.offsetWidth;
        const contentWidth = marqueeContentRef.current.scrollWidth;
        setMaxScroll(contentWidth - containerWidth);
      }
    };

    window.addEventListener("resize", handleResize);

    // Set up ResizeObserver for the marquee container
    if (marqueeRef.current && !marqueeResizeObserver.current) {
      marqueeResizeObserver.current = new ResizeObserver(() => {
        startMarqueeAnimation();
        // Recalculate max scroll
        if (marqueeContentRef.current && marqueeRef.current) {
          const containerWidth = marqueeRef.current.offsetWidth;
          const contentWidth = marqueeContentRef.current.scrollWidth;
          setMaxScroll(contentWidth - containerWidth);
        }
      });
      marqueeResizeObserver.current.observe(marqueeRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (marqueeResizeObserver.current) {
        marqueeResizeObserver.current.disconnect();
      }
    };
  }, [startMarqueeAnimation]);

  // Calculate max scroll when displayAgents changes
  useEffect(() => {
    if (marqueeContentRef.current && marqueeRef.current) {
      const containerWidth = marqueeRef.current.offsetWidth;
      const contentWidth = marqueeContentRef.current.scrollWidth;
      setMaxScroll(contentWidth - containerWidth);
    }
  }, [displayAgents]);

  // Function to scroll marquee left
  const scrollLeft = () => {
    // Update interaction timestamp
    setLastInteractionTime(Date.now());

    if (marqueeContentRef.current) {
      // If auto-scroll is running, pause it first
      if (!isMarqueePaused) {
        setIsMarqueePaused(true);
      }

      const newPosition = Math.max(0, scrollPosition - 200);
      setScrollPosition(newPosition);
      marqueeContentRef.current.style.transform = `translateX(-${newPosition}px)`;
      marqueeContentRef.current.style.transition = "transform 0.3s ease";
      marqueeContentRef.current.style.animation = "none"; // Remove animation
    }
  };

  // Function to scroll marquee right
  const scrollRight = () => {
    // Update interaction timestamp
    setLastInteractionTime(Date.now());

    if (marqueeContentRef.current) {
      // If auto-scroll is running, pause it first
      if (!isMarqueePaused) {
        setIsMarqueePaused(true);
      }

      const newPosition = Math.min(maxScroll, scrollPosition + 200);
      setScrollPosition(newPosition);
      marqueeContentRef.current.style.transform = `translateX(-${newPosition}px)`;
      marqueeContentRef.current.style.transition = "transform 0.3s ease";
      marqueeContentRef.current.style.animation = "none"; // Remove animation
    }
  };

  // Function to toggle marquee pause/play
  const toggleMarqueePause = () => {
    // Update interaction timestamp when user manually toggles
    setLastInteractionTime(Date.now());

    const newPausedState = !isMarqueePaused;
    setIsMarqueePaused(newPausedState);

    if (marqueeContentRef.current) {
      if (newPausedState) {
        // When pausing, switch to manual transform
        marqueeContentRef.current.style.animation = "none";
        marqueeContentRef.current.style.transform = `translateX(-${scrollPosition}px)`;
      } else {
        // When playing, restart animation from beginning
        marqueeContentRef.current.style.transform = "translateX(0)";
        marqueeContentRef.current.style.animation = marqueeAnimation;
        marqueeContentRef.current.style.animationPlayState = "running";
        setScrollPosition(0);
      }
    }
  };

  // Auto-resume marquee after 5 seconds of inactivity
  useEffect(() => {
    let resumeTimer;

    if (isMarqueePaused) {
      // Set a timer to resume auto-play after 5 seconds of no interaction
      resumeTimer = setTimeout(() => {
        const timeSinceLastInteraction = Date.now() - lastInteractionTime;

        // Only resume if it's been more than 5 seconds since last interaction
        if (timeSinceLastInteraction >= 5000) {
          // Check if we're at the end of the scroll
          if (scrollPosition >= maxScroll) {
            // If at the end, reset to start and resume
            setScrollPosition(0);
            setIsMarqueePaused(false);

            if (marqueeContentRef.current) {
              marqueeContentRef.current.style.transform = "translateX(0)";
              marqueeContentRef.current.style.animation = marqueeAnimation;
              marqueeContentRef.current.style.animationPlayState = "running";
            }
          } else {
            // Not at the end, just resume auto-play
            setIsMarqueePaused(false);

            if (marqueeContentRef.current) {
              // Restart animation from current position by calculating appropriate animation
              const containerWidth = marqueeRef.current?.offsetWidth || 0;
              const contentWidth = marqueeContentRef.current.scrollWidth;
              const remainingDistance =
                contentWidth - containerWidth - scrollPosition;

              if (remainingDistance > 0) {
                const duration = Math.max(20, (remainingDistance / 100) * 1.5);
                const animation = `marqueeResume ${duration}s linear forwards`;

                // Create a custom animation for resuming from current position
                marqueeContentRef.current.style.animation = animation;
                marqueeContentRef.current.style.animationPlayState = "running";

                // Add a custom keyframe for resuming
                const styleSheet = document.createElement("style");
                styleSheet.textContent = `
                @keyframes marqueeResume {
                  0% {
                    transform: translateX(-${scrollPosition}px);
                  }
                  100% {
                    transform: translateX(-${contentWidth - containerWidth}px);
                  }
                }
              `;
                document.head.appendChild(styleSheet);

                // Clean up the style element after animation completes
                setTimeout(() => {
                  if (styleSheet.parentNode) {
                    styleSheet.parentNode.removeChild(styleSheet);
                  }
                }, duration * 1000);
              }
            }
          }
        }
      }, 5000); // Check every 5 seconds
    }

    return () => {
      if (resumeTimer) {
        clearTimeout(resumeTimer);
      }
    };
  }, [
    isMarqueePaused,
    lastInteractionTime,
    scrollPosition,
    maxScroll,
    marqueeAnimation,
  ]);

  // Handle hover events for agent names - Updated for static positioning
  const handleAgentNameMouseEnter = (agent, index, e) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Set hovered agent immediately
    setHoveredAgent(agent);

    // Show preview after a small delay (for smoother transition)
    hoverTimeoutRef.current = setTimeout(() => {
      setIsPreviewVisible(true);
    }, 100);
  };

  const handleAgentNameMouseLeave = () => {
    // Clear the hover timeout if it exists
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Hide preview with a small delay to prevent flickering when moving between items
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHovering) {
        setIsPreviewVisible(false);
      }
    }, 150);
  };

  const handlePreviewMouseEnter = () => {
    // Clear any timeout that would hide the preview
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(true);
  };

  const handlePreviewMouseLeave = () => {
    // Set a timeout to hide preview
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      setIsPreviewVisible(false);
    }, 200);
  };

  const handleUserTypeChange = async (type) => {
    setSelectedUserType(type);
    setIsAutoSliding(false);
    setCurrentSlide(0);

    // Clear search when changing user type
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    setSearchQuery("");

    // Update existing agents with new referral links
    if (seniorAgents.length > 0) {
      const updatedAgents = seniorAgents.map((agent) => ({
        ...agent,
        qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
          type === "retail-business"
            ? agent.retailBusinessLink
            : agent.retailAgentLink,
        )}`,
      }));

      setDisplayAgents(updatedAgents);
    }

    // Only resume auto-sliding if on mobile or if there are enough cards on desktop
    const shouldResume =
      !window.matchMedia("(min-width: 992px)").matches ||
      seniorAgents.length >= 3;
    if (shouldResume) {
      setTimeout(() => setIsAutoSliding(true), 5000);
    }
  };

  // Handle search button click - get value from input ref and filter
  const handleSearchClick = () => {
    if (!searchInputRef.current) return;

    const query = searchInputRef.current.value.trim();
    setSearchQuery(query); // Update state for display purposes
    setSearchError(""); // reset error

    if (!query) {
      // If search query is empty, show all agents
      setDisplayAgents(seniorAgents);
      setSearchError("Please enter a search term.");

      // Only resume auto-sliding if on mobile or if there are enough cards on desktop
      const shouldResume =
        !window.matchMedia("(min-width: 992px)").matches ||
        seniorAgents.length >= 3;
      setIsAutoSliding(shouldResume);
      return;
    }

    // Too short
    if (query.length < 3) {
      setSearchError("Search must contain at least 3 characters.");
      return;
    }

    // Too long
    if (query.length > 255) {
      setSearchError("Search must not exceed 255 characters.");
      return;
    }

    // Perform search only when button is clicked
    const searchTerm = query.toLowerCase();
    const results = seniorAgents.filter(
      (agent) =>
        agent.fullName.toLowerCase().includes(searchTerm) ||
        agent.location.toLowerCase().includes(searchTerm) ||
        (agent.agentCode &&
          agent.agentCode.toLowerCase().includes(searchTerm)) ||
        (agent.email && agent.email.toLowerCase().includes(searchTerm)),
    );

    setDisplayAgents(results);
    setCurrentSlide(0); // Reset to first slide
    setIsAutoSliding(false); // Always stop auto sliding when showing search results
  };

  // Handle reset search - show all agents again
  const handleResetSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
      searchInputRef.current.focus(); // Keep focus on input after reset
    }
    setSearchQuery("");
    setSearchError(""); //  clear error
    setDisplayAgents(seniorAgents);
    setCurrentSlide(0);

    // Only resume auto-sliding if on mobile or if there are enough cards on desktop
    const shouldResume =
      !window.matchMedia("(min-width: 992px)").matches ||
      seniorAgents.length >= 3;
    setIsAutoSliding(shouldResume);
  };

  // Handle card click - open appropriate referral link
  const handleCardClick = (link) => {
    window.open(link, "_blank");
  };

  // Handle copy referral link
  const handleCopyLink = (link, e) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(link)
      .then(() => {
        alert("Referral link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  // Get the appropriate referral link based on selected user type
  const getAgentReferralLink = (agent) => {
    return selectedUserType === "retail-business"
      ? agent.retailBusinessLink
      : agent.retailAgentLink;
  };

  // Get the appropriate join text
  const getJoinText = (agentName) => {
    const userTypeText =
      selectedUserType === "retail-business"
        ? "as a Retail Business"
        : "as a Retail Agent";
    return `Join under ${agentName.split(" ")[0]} ${userTypeText}`;
  };

  // Function to scroll to login section
  const scrollToLogin = () => {
    const loginSection = document.getElementById("login-section");
    if (loginSection) {
      const yOffset = -80; // Adjust this value for fine-tuning
      const y =
        loginSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  // Sliding Cards Section Component
  const SlidingCardsSection = () => {
    return (
      <div
        className="sliding-cards-section py-4 pt-5"
        style={{
          backgroundColor: "#001964",
          marginBottom: "40px",
          borderRadius: "20px",
          padding: "30px",
        }}
      >
        <div className="container">
          <h2
            className="text-center mb-4"
            style={{
              fontWeight: "bold",
              fontSize: "2.2rem",
              color: "#fff",
              textShadow: "0 2px 10px rgba(0,0,0,0.3)",
            }}
          >
            {/* Welcome to TopCoin UK - Become a Participating Retail Business or
            Independent Agent Help businesses grow with TopCoin UK and receive
            performance-based rewards */}
            Welcome to TopCoin UK – Become a Participating Retail Business or
            Independent Promotional Agent Help businesses grow through
            structured advertising and customer loyalty activity with TopCoin
            UK. Participating retail businesses and approved independent agents
            may receive promotional incentives based on programme activity and
            engagement levels, in accordance with company terms.
          </h2>

          <p
            className="text-center mb-5"
            style={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "1.1rem",
              maxWidth: "800px",
              margin: "0 auto",
              lineHeight: "1.6",
            }}
          >
            {/* Our team will guide you through the onboarding process and help you get started with TopCoin UK. */}
            Our team will guide you through the onboarding process and support
            you in getting started with TopCoin UK.
          </p>
          <p
            className="text-center mb-5"
            style={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "1.1rem",
              maxWidth: "800px",
              margin: "0 auto",
              lineHeight: "1.6",
            }}
          >
            Please choose & click an agent to join under.
          </p>

          {/* Login Button Section - Added from LoginPage */}
          {/* <ul className="btns_group unordered_list p-0 justify-content-center align-items-center mb-2">
            <li>
              <button
                className="ico_btn_outline"
                onClick={scrollToLogin}
                style={{
                  padding: "15px 25px",
                  marginBottom: "15px",
                }}
              >
                <span
                  className="btn_label"
                  style={{
                    display: "inline-block",
                    padding: "0px",
                    backgroundColor: "transparent",
                    color: "#001964",
                    borderRadius: "10px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                  }}
                >
                  Login
                </span>
              </button>
            </li>
          </ul>
          <div className="text-center mb-4">
            <span
              style={{
                color: "#fff",
                fontSize: "1rem",
                fontWeight: "500",
                display: "inline-block",
                padding: "4px 12px",
                backgroundColor: "rgba(0, 25, 100, 0.08)",
                borderRadius: "12px",
                border: "1px solid rgba(0, 25, 100, 0.15)",
              }}
            >
              Retail Business & Retail Business Agents
            </span>
          </div> */}

          {/* User Type Selection Tabs */}
          <div className="row justify-content-center mb-5">
            <div className="col-md-6 col-lg-4">
              <div
                style={{
                  display: "flex",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "50px",
                  padding: "5px",
                }}
              >
                <button
                  onClick={() => handleUserTypeChange("retail-business")}
                  style={{
                    flex: 1,
                    padding: "12px 20px",
                    border: "none",
                    borderRadius: "50px",
                    backgroundColor:
                      selectedUserType === "retail-business"
                        ? "#2D4CA8"
                        : "transparent",
                    color:
                      selectedUserType === "retail-business"
                        ? "white"
                        : "rgba(255, 255, 255, 0.8)",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  For Retail Businesses
                </button>
                <button
                  onClick={() => handleUserTypeChange("retail-agent")}
                  style={{
                    flex: 1,
                    padding: "12px 20px",
                    border: "none",
                    borderRadius: "50px",
                    backgroundColor:
                      selectedUserType === "retail-agent"
                        ? "#9F4AFF"
                        : "transparent",
                    color:
                      selectedUserType === "retail-agent"
                        ? "white"
                        : "rgba(255, 255, 255, 0.8)",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  For Retail Agents
                </button>
              </div>
            </div>
          </div>

          {/* Search Section */}
          <div className="row justify-content-center mb-4">
            <div className="col-md-8 col-lg-6">
              <div className="input-group" style={{ marginBottom: "10px" }}>
                <input
                  type="text"
                  ref={searchInputRef}
                  className="form-control"
                  placeholder="Search agents by name or email..."
                  maxlength={255}
                  defaultValue={searchQuery}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSearchClick();
                    }
                  }}
                  onFocus={() => {
                    setIsAutoSliding(false);
                    setSearchError("");
                  }}
                  style={{
                    padding: "12px 20px",
                    borderRadius: "50px 0 0 50px",
                    border: "none",
                    fontSize: "0.95rem",
                    color: "#333",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                  }}
                />
                {/* Reset/Clear Button */}
                <button
                  className="btn"
                  onClick={handleResetSearch}
                  title="Clear search"
                  style={{
                    backgroundColor: searchQuery ? "#ff6b6b" : "#e0e0e0",
                    color: searchQuery ? "white" : "#666",
                    border: "none",
                    borderLeft: "1px solid rgba(0,0,0,0.1)",
                    padding: "12px 15px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (searchQuery) {
                      e.target.style.backgroundColor = "#ff5252";
                    } else {
                      e.target.style.backgroundColor = "#d0d0d0";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (searchQuery) {
                      e.target.style.backgroundColor = "#ff6b6b";
                    } else {
                      e.target.style.backgroundColor = "#e0e0e0";
                    }
                  }}
                >
                  <FaTimes size={14} />
                </button>
                <button
                  className="btn"
                  onClick={handleSearchClick}
                  style={{
                    backgroundColor:
                      selectedUserType === "retail-business"
                        ? "#2D4CA8"
                        : "#9F4AFF",
                    color: "white",
                    border: "none",
                    borderRadius: "0 50px 50px 0",
                    padding: "12px 18px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor =
                      selectedUserType === "retail-business"
                        ? "#1a3a8f"
                        : "#8c3fe6";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor =
                      selectedUserType === "retail-business"
                        ? "#2D4CA8"
                        : "#9F4AFF";
                  }}
                >
                  <FaSearch />
                </button>
              </div>
              {searchError && (
                <div
                  style={{
                    marginTop: "-10px",
                    textAlign: "center",
                    color: "#ff6b6b",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                  }}
                >
                  {searchError}
                </div>
              )}
            </div>
            <div className="text-center mb-0">
              <p
                style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "1rem" }}
              >
                {selectedUserType === "retail-business"
                  ? "Join under our Authorised Senior Retail Business Agents and receive performance-based rewards."
                  : "Join under our Authorised Senior Retail Business Agents and receive performance-based rewards."}
              </p>
            </div>
            {/* Business Agent List (show 5, scroll for more) */}
            <div className="col-lg-6 row justify-content-center mb-3">
              <div className="col-md-8 col-lg-6"></div>
              <div
                style={{
                  maxHeight: "320px",
                  overflowY:
                    displayBusinessAgents.length > 5 ? "auto" : "visible",
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                  background: "#f9f9f9",
                  marginTop: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  padding: "8px 0",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: "#001964",
                    fontSize: "1rem",
                    padding: "2px 18px 6px 18px",
                    borderBottom: "1px solid #e0e0e0",
                    background: "#f3f6fa",
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                    marginBottom: "2px",
                  }}
                >
                  Retail Business Agents
                </div>

                {/* Loading state */}
                {isLoadingBusinessAgents && (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    Loading agents...
                  </div>
                )}

                {/* Error state */}
                {businessAgentsError && (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#d32f2f",
                    }}
                  >
                    {businessAgentsError}
                  </div>
                )}

                {/* Agents list */}
                {!isLoadingBusinessAgents &&
                  !businessAgentsError &&
                  displayBusinessAgents.length === 0 && (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      No agents found.
                    </div>
                  )}

                {!isLoadingBusinessAgents &&
                  !businessAgentsError &&
                  [...displayBusinessAgents]
                    .sort((a, b) => a.fullName.localeCompare(b.fullName))
                    .map((agent, idx, arr) => (
                      <div
                        key={agent.id}
                        style={{
                          padding: "0px 15px",
                          borderBottom:
                            idx !== arr.length - 1 ? "1px solid #eee" : "none",
                          background: "white",
                          borderRadius: "6px",
                          margin: "0 8px 6px 8px",
                          fontWeight: 600,
                          color: "#001964",
                          fontSize: "0.9rem",
                          textAlign: "left",
                          letterSpacing: "0.01em",
                        }}
                      >
                        {agent.fullName} ({agent.location})
                      </div>
                    ))}

                {displayBusinessAgents.length > 5 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "8px",
                      color: "#2D4CA8",
                      fontWeight: 500,
                      fontSize: "0.95rem",
                    }}
                  >
                    Scroll to see more agents...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* <div className="text-center mb-4">
            <p style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "1rem" }}>
              {selectedUserType === "retail-business"
                ? "Join under our Authorised Senior Retail Business Agents and receive performance-based rewards."
                : "Join under our Authorised Senior Retail Business Agents and receive performance-based rewards."}
            </p>
          </div> */}

          {/* Running Agent Names Marquee with Manual Scroll Buttons */}
          {!isLoadingAgents && displayAgents.length > 0 && (
            <div className="row justify-content-center mb-4">
              <div className="col-12 position-relative">
                {/* Control Buttons Container - Positioned above the marquee */}
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    backgroundColor: "rgba(0, 25, 100, 0.9)",
                    padding: "8px 15px",
                    borderRadius: "20px",
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  {/* Left Scroll Button */}
                  <button
                    onClick={scrollLeft}
                    disabled={scrollPosition <= 0}
                    style={{
                      backgroundColor:
                        scrollPosition <= 0
                          ? "rgba(255, 255, 255, 0.1)"
                          : "#2D4CA8",
                      color: "white",
                      border: "none",
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: scrollPosition <= 0 ? "not-allowed" : "pointer",
                      opacity: scrollPosition <= 0 ? 0.5 : 1,
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                    }}
                    onMouseEnter={(e) => {
                      if (scrollPosition > 0) {
                        e.target.style.backgroundColor = "#1a3a8f";
                        e.target.style.transform = "scale(1.1)";
                        e.target.style.boxShadow =
                          "0 4px 12px rgba(0, 0, 0, 0.3)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (scrollPosition > 0) {
                        e.target.style.backgroundColor = "#2D4CA8";
                        e.target.style.transform = "scale(1)";
                        e.target.style.boxShadow =
                          "0 2px 8px rgba(0, 0, 0, 0.2)";
                      }
                    }}
                    title="Scroll left"
                  >
                    <IoArrowBack size={18} />
                  </button>

                  {/* Right Scroll Button */}
                  <button
                    onClick={scrollRight}
                    disabled={scrollPosition >= maxScroll}
                    style={{
                      backgroundColor:
                        scrollPosition >= maxScroll
                          ? "rgba(255, 255, 255, 0.1)"
                          : "#2D4CA8",
                      color: "white",
                      border: "none",
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor:
                        scrollPosition >= maxScroll ? "not-allowed" : "pointer",
                      opacity: scrollPosition >= maxScroll ? 0.5 : 1,
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                    }}
                    onMouseEnter={(e) => {
                      if (scrollPosition < maxScroll) {
                        e.target.style.backgroundColor = "#1a3a8f";
                        e.target.style.transform = "scale(1.1)";
                        e.target.style.boxShadow =
                          "0 4px 12px rgba(0, 0, 0, 0.3)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (scrollPosition < maxScroll) {
                        e.target.style.backgroundColor = "#2D4CA8";
                        e.target.style.transform = "scale(1)";
                        e.target.style.boxShadow =
                          "0 2px 8px rgba(0, 0, 0, 0.2)";
                      }
                    }}
                    title="Scroll right"
                  >
                    <IoArrowForward size={18} />
                  </button>
                </div>

                {/* Marquee Container */}
                <div
                  ref={marqueeRef}
                  style={{
                    position: "relative",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    padding: "15px 20px",
                    overflow: "hidden",
                    minHeight: "50px",
                    display: "flex",
                    alignItems: "center",
                    marginTop: "70px",
                  }}
                >
                  <div
                    ref={marqueeContentRef}
                    style={{
                      display: "flex",
                      animation: !isMarqueePaused ? marqueeAnimation : "none",
                      whiteSpace: "nowrap",
                      gap: "25px",
                      padding: "0 10px",
                      width: "max-content",
                      animationPlayState: isMarqueePaused
                        ? "paused"
                        : "running",
                      transform: isMarqueePaused
                        ? `translateX(-${scrollPosition}px)`
                        : "translateX(0)",
                      transition: "transform 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isMarqueePaused) {
                        e.currentTarget.style.animationPlayState = "paused";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMarqueePaused) {
                        e.currentTarget.style.animationPlayState = "running";
                      }
                    }}
                  >
                    {/* Display agents 3 times for seamless looping */}
                    {[...displayAgents, ...displayAgents, ...displayAgents].map(
                      (agent, index) => (
                        <span
                          key={`marquee-${agent.id}-${index}`}
                          onMouseEnter={(e) =>
                            handleAgentNameMouseEnter(agent, index, e)
                          }
                          onMouseLeave={handleAgentNameMouseLeave}
                          style={{
                            color: "white",
                            fontSize: "0.9rem",
                            fontWeight: "500",
                            cursor: "pointer",
                            padding: "8px 15px",
                            borderRadius: "20px",
                            backgroundColor: "rgba(255, 255, 255, 0.12)",
                            transition: "all 0.2s ease",
                            border: `1px solid ${agent.color}40`,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            flexShrink: 0,
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = `${agent.color}40`;
                            e.currentTarget.style.transform = "scale(1.03)";
                            e.currentTarget.style.boxShadow = `0 3px 8px ${agent.color}40`;
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(255, 255, 255, 0.12)";
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          onClick={() =>
                            handleCardClick(getAgentReferralLink(agent))
                          }
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: agent.color,
                              display: "inline-block",
                            }}
                          />
                          {agent.shortName || agent.fullName}
                          <span
                            style={{
                              fontSize: "0.7rem",
                              opacity: 0.7,
                              marginLeft: "3px",
                            }}
                          >
                            ({agent.location})
                          </span>
                        </span>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Agent Preview Popup - FIXED STATIC POSITION */}
          {isPreviewVisible && hoveredAgent && (
            <div
              className="agent-preview"
              onMouseEnter={handlePreviewMouseEnter}
              onMouseLeave={handlePreviewMouseLeave}
              style={{
                position: "fixed",
                left: "50%",
                top: window.innerWidth <= 768 ? "50%" : "250px", // Fixed position for large devices
                transform:
                  window.innerWidth <= 768
                    ? "translate(-50%, -50%)"
                    : "translateX(-50%)",
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.25)",
                zIndex: 9999,
                width: window.innerWidth <= 768 ? "90vw" : "240px",
                maxWidth: "280px",
                padding: "15px",
                animation: "fadeIn 0.2s ease",
                border: `2px solid ${hoveredAgent.color}`,
                // Mobile-specific styles
                ...(window.innerWidth <= 768 && {
                  position: "fixed",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "90%",
                  maxWidth: "300px",
                  maxHeight: "80vh",
                  overflowY: "auto",
                }),
              }}
            >
              {/* Close button for mobile */}
              {window.innerWidth <= 7688 && (
                <button
                  onClick={() => {
                    setIsHovering(false);
                    setIsPreviewVisible(false);
                  }}
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    color: "#666",
                    cursor: "pointer",
                    zIndex: 10000,
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    backgroundColor: "rgba(0,0,0,0.05)",
                  }}
                >
                  ×
                </button>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <img
                  src={hoveredAgent.profilePic}
                  alt={hoveredAgent.fullName}
                  style={{
                    width: window.innerWidth <= 768 ? "60px" : "45px",
                    height: window.innerWidth <= 768 ? "60px" : "45px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: `2px solid ${hoveredAgent.color}`,
                    marginRight: "10px",
                  }}
                />
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      margin: 0,
                      color: "#001964",
                      fontSize: window.innerWidth <= 768 ? "1rem" : "0.9rem",
                      fontWeight: "600",
                      lineHeight: "1.2",
                    }}
                  >
                    {hoveredAgent.fullName}
                  </h4>
                  <p
                    style={{
                      margin: "3px 0 0 0",
                      color: hoveredAgent.color,
                      fontSize:
                        window.innerWidth <= 768 ? "0.85rem" : "0.75rem",
                      fontWeight: "500",
                    }}
                  >
                    {hoveredAgent.position}
                  </p>
                  <p
                    style={{
                      margin: "2px 0 0 0",
                      color: "#666",
                      fontSize: window.innerWidth <= 768 ? "0.8rem" : "0.7rem",
                    }}
                  >
                    📍 {hoveredAgent.location}
                  </p>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: `${hoveredAgent.color}10`,
                  padding: "8px",
                  borderRadius: "6px",
                  marginBottom: "10px",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    color: "#001964",
                    fontSize: window.innerWidth <= 768 ? "0.85rem" : "0.75rem",
                    textAlign: "center",
                    fontWeight: "500",
                    lineHeight: "1.3",
                  }}
                >
                  {getJoinText(hoveredAgent.fullName)}
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "10px",
                }}
              >
                <img
                  src={hoveredAgent.qrCode}
                  alt="QR Code"
                  style={{
                    width: window.innerWidth <= 768 ? "100px" : "80px",
                    height: window.innerWidth <= 768 ? "100px" : "80px",
                    borderRadius: "6px",
                    border: `1px solid ${hoveredAgent.color}`,
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: "6px", marginTop: "10px" }}>
                <button
                  onClick={(e) =>
                    handleCopyLink(getAgentReferralLink(hoveredAgent), e)
                  }
                  style={{
                    flex: 1,
                    backgroundColor: hoveredAgent.color,
                    color: "white",
                    border: "none",
                    padding: window.innerWidth <= 768 ? "8px 10px" : "6px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: window.innerWidth <= 768 ? "0.8rem" : "0.7rem",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.opacity = "0.9";
                    e.target.style.transform = "translateY(-1px)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.opacity = "1";
                    e.target.style.transform = "translateY(0)";
                  }}
                >
                  <FaCopy size={window.innerWidth <= 768 ? 14 : 12} /> Copy
                </button>
                <button
                  onClick={() =>
                    handleCardClick(getAgentReferralLink(hoveredAgent))
                  }
                  style={{
                    flex: 1,
                    backgroundColor: "#001964",
                    color: "white",
                    border: "none",
                    padding: window.innerWidth <= 768 ? "8px 10px" : "6px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: window.innerWidth <= 768 ? "0.8rem" : "0.7rem",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.opacity = "0.9";
                    e.target.style.transform = "translateY(-1px)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.opacity = "1";
                    e.target.style.transform = "translateY(0)";
                  }}
                >
                  <FaLink size={window.innerWidth <= 768 ? 14 : 12} /> Join
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoadingAgents && (
            <div className="text-center">
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Loading agents...</span>
              </div>
            </div>
          )}

          {/* No Results State */}
          {!isLoadingAgents && displayAgents.length === 0 && (
            <div className="text-center">
              <p style={{ color: "#ff6b6b", fontSize: "1.1rem" }}>
                No agents found matching your search criteria.
              </p>
              <button
                onClick={handleResetSearch}
                style={{
                  backgroundColor: "transparent",
                  color: "#4CAF50",
                  border: "1px solid #4CAF50",
                  borderRadius: "20px",
                  padding: "8px 20px",
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  marginTop: "10px",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#4CAF50";
                  e.target.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#4CAF50";
                }}
              >
                Show All Agents
              </button>
            </div>
          )}

          {/* Error State */}
          {agentsError && !isLoadingAgents && (
            <div className="text-center">
              <p style={{ color: "#ff6b6b" }}>{agentsError}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <div className="index_ico page_wrapper">
        <Header />
        <main className="page_content">
          <PageTitle2 pageTitle={"Work With TCUK"} pagesub={"Home"} />

          {/* Sliding Cards Section */}
          <SlidingCardsSection />

          {/* Add an anchor point for login section */}
          <div id="login-section" style={{ paddingTop: "20px" }}></div>

          <hr
            style={{
              padding: 0,
              margin: 0,
              height: "1px",
              backgroundColor: "gray",
            }}
          />
          <div id="business-login"></div>
          {/*Business Login*/}
          <RetailBusiness />

          <hr
            style={{
              padding: 0,
              margin: 0,
              height: "1px",
              backgroundColor: "gray",
            }}
          />
          <div id="business-agent-login"></div>
          {/*Business Agents Login*/}
          <RetailAgent />
        </main>
        <Footer />
        <Scrollbar />
      </div>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
          
          @keyframes marquee {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(calc(-100% / 3));
            }
          }
          
          .agent-preview {
            animation: fadeIn 0.2s ease;
          }
          
          /* Hide scroll buttons on very small screens */
          @media (max-width: 576px) {
            .scroll-buttons {
              display: none !important;
            }
          }
        `}
      </style>
    </Fragment>
  );
}

export default WorkWithTCUKPage;
